/**
 * Drives `PastSeasonsDialog` from `?dialog=past-seasons&seasonId=`,
 * `useSeasonList` and `useSeasonLeaderboardGet` (ISSUE-754, S8, S9). Errors
 * from the leaderboard read go through the shared error modal, mirroring
 * `PoolSeasonCardContainer`'s list effect. The server's list order and every
 * leaderboard number pass through untouched: no sum, sort or rank lives here.
 */
import {
  useSeasonLeaderboardGet,
  useSeasonList,
} from "@sportsmet/contest-sdk/private";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type JSX, useEffect } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import { useOverlay } from "../../routeState/useOverlay";
import { PastSeasonsDialog } from "./PastSeasonsDialog";
import {
  buildPastSeasonRows,
  closePastSeasonSearch,
  openPastSeasonSearch,
  PAST_SEASONS_DIALOG_NAME,
  PAST_SEASONS_DIALOG_TITLE,
  resolvePastSeasonSummaryPhase,
  seasonDateRangeLabel,
} from "./pastSeasonsModel";

export type typePastSeasonsDialogContainerProps = {
  poolId: string;
};

export function PastSeasonsDialogContainer(
  props: typePastSeasonsDialogContainerProps,
): JSX.Element {
  const { poolId } = props;

  const navigate = useNavigate();
  const overlay = useOverlay();

  const open = overlay.isOpen("dialog", PAST_SEASONS_DIALOG_NAME);
  const search = useSearch({ strict: false });
  const seasonId =
    open && typeof search.seasonId === "string" && search.seasonId !== ""
      ? search.seasonId
      : null;

  const list = useSeasonList({ poolId }, { enabled: open });
  const leaderboard = useSeasonLeaderboardGet(
    { poolId, seasonId: seasonId ?? "" },
    { enabled: seasonId !== null },
  );

  useEffect(() => {
    if (extractErrorCode(leaderboard.data) !== null) {
      showApiError(leaderboard.data, undefined, leaderboard.dataUpdatedAt);
    }
  }, [leaderboard.dataUpdatedAt, seasonId]);

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const rows = buildPastSeasonRows(list.data?.data?.items ?? [], timeZone);
  const summaryPhase = resolvePastSeasonSummaryPhase({
    isLoading: leaderboard.isLoading,
    isError: leaderboard.isError,
    errorCode: extractErrorCode(leaderboard.data),
    memberCount: leaderboard.data?.data?.members.length ?? 0,
  });
  const summaryMembers = leaderboard.data?.data?.members ?? [];

  const season = leaderboard.data?.data?.season ?? null;
  const summaryTitle = season?.seasonTitle ?? PAST_SEASONS_DIALOG_TITLE;
  const summaryDescription = season
    ? seasonDateRangeLabel({
        startedAt: season.startedAt,
        endedAt: season.endedAt,
        timeZone,
      })
    : undefined;

  const step: "list" | "summary" = seasonId === null ? "list" : "summary";

  function onSelectSeason(id: string): void {
    void navigate({
      to: ".",
      replace: true,
      search: (previous: unknown) =>
        openPastSeasonSearch(
          { ...((previous ?? {}) as Record<string, unknown>) },
          id,
        ),
    });
  }

  function onBackToList(): void {
    void navigate({
      to: ".",
      replace: true,
      search: (previous: unknown) =>
        closePastSeasonSearch({
          ...((previous ?? {}) as Record<string, unknown>),
        }),
    });
  }

  function onRetry(): void {
    void leaderboard.refetch();
  }

  return (
    <PastSeasonsDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) overlay.close({ kind: "dialog", clearParams: ["seasonId"] });
      }}
      step={step}
      rows={rows}
      onSelectSeason={onSelectSeason}
      summaryTitle={summaryTitle}
      summaryDescription={summaryDescription}
      summaryPhase={summaryPhase}
      summaryMembers={summaryMembers}
      onBackToList={onBackToList}
      onRetry={onRetry}
    />
  );
}
